import React from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {getTransportParticipantsList} from "../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import Loader from "../core/Loader";
import Grid from '@material-ui/core/Grid';
import {Mail, Person, Phone} from '@material-ui/icons';
import {Accordion, AccordionDetails, AccordionSummary, Avatar, ListItem, ListItemIcon, ListItemText, Paper} from "@material-ui/core";
import {InfoCardLine} from "../page-single-shipment-tracking/ShipmentInfoCard";
import withWidth, {WithWidthProps} from "@material-ui/core/withWidth";
import List from "@material-ui/core/List";

interface ITransportParticipant {
    contactId?: number
    partyId?: number
    accountNumber?: string
    address?: string
    zipCode?: string
    city?: string
    country?: string
    partyName?: string
    partyType?: string
    partySequence?: number
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    mobile?: string
    contactType?: string
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            "& .MuiExpansionPanelDetails-root": {
                padding: 5,
                backgroundColor: "#fafafa"
            },
            "& .MuiPaper-root": {
                height: " 100%",
            }
        },
        title: {
            display: "flex",
            alignItems: "center",
            "& .MuiSvgIcon-root": {
                marginRight: 10
            }
        },
        link: {
            display: "flex",
            alignItems: "center",
            "& a": {
                textDecoration: "underline",
                color: theme.palette.primary.main,
                fontSize: 15
            }
        }
    }),
);

const ParticipantsExpansionPanel: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    return (
        <div className={classes.root}>
            <Accordion TransitionProps={{unmountOnExit: true}}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    aria-controls="participants-content"
                    id="participants-header"
                >
                    <div className={classes.title}>
                        <Person/>
                        <Typography variant="button">{t("participant.caption")}</Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails >
                    <ParticipantsGrid uuid={uuid} width={width}/>
                </AccordionDetails >
            </Accordion>
        </div>
    );
};


const ParticipantsGrid: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {

    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const classes = useStyles();

    const [shipmentParticipants, setShipmentParticipants] = React.useState<ITransportParticipant[] | undefined>(undefined);


    React.useEffect(() => {

        getTransportParticipantsList(uuid).then(({data}) => {
            const {status, response} = data;


            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentParticipants(response);
            }
        })
    }, [enqueueSnackbar, t, setShipmentParticipants, uuid]);

    if (!shipmentParticipants) {
        return <div className={classes.root}>
            <Loader/>
        </div>;
    }

    return <Grid
        container
        direction="row"
        spacing={1}
    >

        {shipmentParticipants?.map((participant, index) => <Grid item key={index} xs={12} sm={6}><ParticipantCard
            participant={participant}
            width={width}
        /></Grid>)}
    </Grid>
};


const ParticipantCard: React.FC<{ participant: ITransportParticipant } & WithWidthProps> = ({participant, width}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const {partyType, partyId, accountNumber, partyName, address, zipCode, city, country, email, phone} = participant;

    return (<Paper> <List>
            <ListItem>
                {width !== "xs" && <ListItemIcon>
                    <Avatar aria-label="participant">
                        <Person/>
                    </Avatar>
                </ListItemIcon>}
                <ListItemText
                    primary={
                        <div>
                            <InfoCardLine property={t("party.type." + partyType)}
                                          value={(partyId ? partyId : "") + " " + (accountNumber ? `(${accountNumber})` : "")}
                                          width={width}/>
                            <InfoCardLine value={partyName} width={width}/>

                            <InfoCardLine value={address} width={width}/>

                            <InfoCardLine value={(zipCode ? zipCode : "") + " " + (city ? `(${city})` : "")}
                                          width={width}/>

                            <InfoCardLine value={country} width={width}/>

                            {email && <div className={classes.link}>
                                <Mail color="primary"/>
                                <ListItem button color="primary" key="Email" component="a"
                                          href={`mailto:${email}`}>{email}</ListItem>
                            </div>}

                            {phone && <div className={classes.link}>
                                <Phone color="primary"/>
                                <ListItem button color="primary" key="Phone" component="a"
                                          href={`tel:${phone}`}>{phone}</ListItem>
                            </div>}
                        </div>
                    }
                />
            </ListItem>

        </List>
        </Paper>
    );
};

export default withWidth()(ParticipantsExpansionPanel);